import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Tag } from 'antd';
import {
    VideoCameraOutlined,
    LockOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import moment from 'moment';
import { useReports } from '../context/ReportsContext';
import { getMeetingsReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

const PALETTE = ['#00AAF1', '#10b981', '#6366f1', '#a855f7', '#f59e0b', '#ec4899'];
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

const FALLBACK_MEETINGS_DATA = {
    foundProject: true,
    isFallback: true,
    project: {
        name: 'DymaxTech Team Meeting Minutes',
        projectCode: 'PRJ-9246',
        code: 'PRJ-9246',
        system_project_type: 'TEAM_MEETINGS',
    },
    totalMeetingHours: '23.5h',
    totalActionItems: 19,
    categories: [
        { name: 'Sprint Planning & Backlog Grooming', hours: '6.5h', hoursValue: 6.5, minutes: 390, sessions: 4, percentage: 28 },
        { name: 'Daily Engineering Standup', hours: '5.5h', hoursValue: 5.5, minutes: 330, sessions: 11, percentage: 23 },
        { name: 'Architecture & Tech RFC Review', hours: '4.5h', hoursValue: 4.5, minutes: 270, sessions: 3, percentage: 19 },
        { name: 'Client Status & Demo Sync', hours: '4.0h', hoursValue: 4.0, minutes: 240, sessions: 2, percentage: 17 },
        { name: 'Cross-Functional Alignment', hours: '3.0h', hoursValue: 3.0, minutes: 180, sessions: 2, percentage: 13 },
    ],
    dailyDensity: [
        { date: '2026-10-14', hours: 2.5, sessions: 3, actionItems: 4 },
        { date: '2026-10-15', hours: 3.5, sessions: 4, actionItems: 3 },
        { date: '2026-10-16', hours: 2.0, sessions: 2, actionItems: 2 },
        { date: '2026-10-17', hours: 4.0, sessions: 4, actionItems: 5 },
        { date: '2026-10-20', hours: 3.0, sessions: 3, actionItems: 1 },
        { date: '2026-10-21', hours: 4.5, sessions: 5, actionItems: 4 },
        { date: '2026-10-22', hours: 2.0, sessions: 2, actionItems: 2 },
        { date: '2026-10-23', hours: 2.0, sessions: 2, actionItems: 1 },
    ],
    meetings: [
        {
            id: 'fb-m1',
            title: 'Sprint 44 Backlog Grooming & Velocity Planning',
            category: 'Sprint Planning & Backlog Grooming',
            date: '2026-10-23T10:00:00.000Z',
            duration: '90 mins',
            durationMin: 90,
            organizer: 'Muhammad Bilal',
            attendeesCount: 8,
            attendeesList: ['Muhammad Bilal', 'Sarah Chen', 'Tahir Khan', 'Priyah Patel', 'David Kim'],
            actionItemsCount: 5,
            status: 'Completed',
        },
        {
            id: 'fb-m2',
            title: 'Core Architecture RFC: Caching & Query Optimization Layer',
            category: 'Architecture & Tech RFC Review',
            date: '2026-10-22T14:30:00.000Z',
            duration: '75 mins',
            durationMin: 75,
            organizer: 'Ali Haider',
            attendeesCount: 5,
            attendeesList: ['Ali Haider', 'Muhammad Bilal', 'Zuhaib Abbas'],
            actionItemsCount: 3,
            status: 'Completed',
        },
        {
            id: 'fb-m3',
            title: 'Daily Core Engineering Standup & Blocker Resolution',
            category: 'Daily Engineering Standup',
            date: '2026-10-22T09:30:00.000Z',
            duration: '30 mins',
            durationMin: 30,
            organizer: 'Muhammad Bilal',
            attendeesCount: 9,
            attendeesList: ['Muhammad Bilal', 'Tahir Khan', 'Priyah Patel', 'David Kim'],
            actionItemsCount: 2,
            status: 'Completed',
        },
        {
            id: 'fb-m4',
            title: 'Enterprise Client Milestone Q4 Demo & Feedback Sync',
            category: 'Client Status & Demo Sync',
            date: '2026-10-21T16:00:00.000Z',
            duration: '60 mins',
            durationMin: 60,
            organizer: 'Sarah Chen',
            attendeesCount: 6,
            attendeesList: ['Sarah Chen', 'Ali Haider', 'Michael Vance'],
            actionItemsCount: 4,
            status: 'Completed',
        },
        {
            id: 'fb-m5',
            title: 'Cross-Functional Design System & Token Alignment',
            category: 'Cross-Functional Alignment',
            date: '2026-10-20T11:00:00.000Z',
            duration: '45 mins',
            durationMin: 45,
            organizer: 'Priyah Patel',
            attendeesCount: 6,
            attendeesList: ['Priyah Patel', 'Muhammad Bilal', 'Sarah Chen'],
            actionItemsCount: 3,
            status: 'Completed',
        },
        {
            id: 'fb-m6',
            title: 'Database Sharding & Connection Pool Tuning Review',
            category: 'Architecture & Tech RFC Review',
            date: '2026-10-17T15:00:00.000Z',
            duration: '60 mins',
            durationMin: 60,
            organizer: 'Ali Haider',
            attendeesCount: 4,
            attendeesList: ['Ali Haider', 'Zuhaib Abbas'],
            actionItemsCount: 2,
            status: 'Completed',
        },
    ],
};

export default function MeetingsTab() {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(() => getCachedTab('meetings'));

    const loadData = useCallback(async () => {
        const cached = getCachedTab('meetings');
        if (cached) {
            setData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getMeetingsReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                teamMember: filters.teamMember,
                department: filters.department,
            });
            if (res && res.success && res.result) {
                setData(res.result);
                setCachedTab('meetings', res.result);
            }
        } catch (err) {
            console.error('Error fetching meetings report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const isFallback = Boolean(
        data?.isFallback ||
        !data?.foundProject ||
        (!data?.categories?.length && !data?.meetings?.length) ||
        (data?.categories?.every(c => !c.sessions && !c.minutes) && !data?.meetings?.length)
    );
    const activeData = isFallback ? FALLBACK_MEETINGS_DATA : (data || FALLBACK_MEETINGS_DATA);

    const projectInfo = activeData?.project || {
        name: 'DymaxTech Team Meeting Minutes',
        projectCode: 'PRJ-9246',
    };

    const categories = activeData?.categories || [];
    const dailyDensity = (activeData?.dailyDensity || []).map(d => ({
        ...d,
        label: d.label || moment(d.date).format('ddd D'),
    }));
    const meetings = activeData?.meetings || [];
    const totalHours = activeData?.totalMeetingHours || '0h';
    const totalActionItems = activeData?.totalActionItems || 0;

    if (loading && !data) {
        return (
            <div className="py-20 text-center bg-white dark:bg-black rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                <Spin size="large" />
                <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">
                    Loading meeting analytics &amp; milestone records...
                </p>
            </div>
        );
    }

    const nonZeroCategories = categories.filter(c => c.minutes > 0 || c.sessions > 0);

    return (
        <div className="space-y-6">
            {/* Demonstration mode notification pill */}
            {isFallback && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs">
                    <div className="flex items-center gap-2 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                        <span>Simulated Telemetry Mode: PRJ-9246 has no live sessions logged for the current filter window. Displaying benchmark milestone sessions &amp; action items.</span>
                    </div>
                    <span className="font-mono text-[11px] uppercase tracking-wider font-bold opacity-80 shrink-0">PRJ-9246 Fallback</span>
                </div>
            )}

            {/* PRJ-9246 Dedicated Project System Banner */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-base shadow-xs">
                        <VideoCameraOutlined />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-600 text-white font-headline">
                                {projectInfo.projectCode || projectInfo.code || 'PRJ-9246'}
                            </span>
                            <h3 className="text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                                {projectInfo.name || 'DymaxTech Team Meeting Minutes'}
                            </h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Each milestone in this project defines the meeting category (e.g. Daily Dev, Client, Tech Sync). Tasks track individual sessions and action items.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                    <span className="px-2.5 py-1 rounded-lg bg-white dark:bg-black border border-slate-200 dark:border-neutral-800 flex items-center gap-1.5">
                        <LockOutlined className="text-blue-500" />
                        <span>Isolated from Billable Client Effort</span>
                    </span>
                    <TabHelpButton tabKey="meetings" />
                </div>
            </div>

            {/* Milestone Category Stat Cards */}
            {categories.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {categories.map((cat, idx) => {
                        const color = PALETTE[idx % PALETTE.length];
                        return (
                            <div
                                key={cat.name}
                                className="p-4 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs"
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Milestone</span>
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }}></span>
                                </div>
                                <div className="text-sm font-extrabold text-slate-900 dark:text-white line-clamp-1 font-headline">
                                    {cat.name}
                                </div>
                                <div className="text-xl font-black font-headline mt-1.5" style={{ color }}>
                                    {cat.hours}{' '}
                                    <span className="text-xs text-slate-400 font-semibold font-sans">
                                        ({cat.sessions} {cat.sessions === 1 ? 'session' : 'sessions'})
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="p-6 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 text-center text-xs text-slate-400">
                    Project <strong>PRJ-9246 (DymaxTech Team Meeting Minutes)</strong> has no milestones or sessions in the active database yet. When connected to the live database, milestone categories will populate automatically.
                </div>
            )}

            {/* Visual Charts: Milestone Category Donut + Daily Meeting Density Bar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Milestone Category Distribution Donut */}
                <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Time by Milestone Category
                        </h3>
                        <p className="text-xs text-slate-400 mb-4">
                            Total tracked meeting hours: <strong className="text-slate-700 dark:text-slate-200">{totalHours}</strong>
                        </p>
                    </div>

                    {nonZeroCategories.length === 0 ? (
                        <div className="h-52 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                            <InboxOutlined style={{ fontSize: 22 }} />
                            <span>No meeting hours recorded yet</span>
                        </div>
                    ) : (
                        <>
                            <div className="h-52">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={nonZeroCategories}
                                            dataKey="minutes"
                                            nameKey="name"
                                            innerRadius="58%"
                                            outerRadius="85%"
                                            paddingAngle={2}
                                            stroke="none"
                                        >
                                            {nonZeroCategories.map((c, i) => (
                                                <Cell key={c.name} fill={PALETTE[i % PALETTE.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...tooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="space-y-2 text-xs font-semibold mt-2">
                                {categories.map((c, i) => (
                                    <div key={c.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                        <span className="flex items-center gap-2 truncate">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                                            <span className="truncate">{c.name}</span>
                                        </span>
                                        <span className="font-extrabold">{c.percentage}% ({c.hours})</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Daily Meeting Density Bar Chart */}
                <div className="lg:col-span-2 bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                Daily Meeting Load &amp; Action Items
                            </h3>
                            <p className="text-xs text-slate-400">
                                Meeting hours and sessions logged per day under PRJ-9246
                            </p>
                        </div>
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-neutral-900 px-3 py-1.5 rounded-xl border border-indigo-200/50 dark:border-neutral-700">
                            {totalActionItems} Action Items Generated
                        </span>
                    </div>

                    <div className="h-60">
                        {dailyDensity.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                                <InboxOutlined style={{ fontSize: 22 }} />
                                <span>No meeting sessions in the selected window</span>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dailyDensity} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} vertical={false} />
                                    <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="h" />
                                    <Tooltip {...tooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="hours" name="Meeting Hours" fill="#00AAF1" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Recorded Meeting Sessions Visual Cards */}
            {meetings.length > 0 && (
                <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline mb-4">
                        Logged Sessions &amp; Action Item Deliverables
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {meetings.slice(0, 9).map((m) => (
                            <div key={m.id} className="p-4 rounded-xl bg-slate-50/70 dark:bg-neutral-950 border border-slate-200/60 dark:border-neutral-800 space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <Tag color="blue">{m.category || 'Meeting'}</Tag>
                                    <span className="text-xs text-slate-400 font-semibold">{m.duration}</span>
                                </div>
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">{m.title}</h4>
                                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                                    <span>{m.attendeesCount} participants</span>
                                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{m.actionItemsCount} subtasks</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
